import { memo, useCallback, useMemo } from 'react';

import {
  useTrackChangedVideoClosedCaptionTrack,
  useTrackClickedVideoLoopButton,
  useTrackClickedVideoQualityButton,
} from '@air/analytics';
import { PLAYBACK_RATES, usePlayerStore } from '@air/component-player';
import { CheckIcon, MoonIcon, SunIcon } from '@air/design-icons';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';

import { SettingsButton } from '~/components/AssetModalV2/Content/Footer/components/SettingsButton';
import { Theme } from '~/components/v2/store/shared/createThemeSlice';

type SelectableAssetModalTheme = Theme.Dark | Theme.Light;

export type VideoSettingsButtonProps = {
  container?: HTMLElement | null;
  includePlaybackRateOptions?: boolean;
  includeSubtitleOptions?: boolean;
  includeThemeOption?: boolean;
  onSelectTheme?: (theme: SelectableAssetModalTheme) => void;
  theme?: Theme;
};

const VideoSettingsButtonComponent = ({
  container,
  includePlaybackRateOptions = false,
  includeSubtitleOptions = false,
  includeThemeOption = false,
  onSelectTheme,
  theme,
}: VideoSettingsButtonProps) => {
  const id = usePlayerStore((state) => state.id);
  const playbackRate = usePlayerStore((state) => state.playbackRate);
  const resolutionLevel = usePlayerStore((state) => state.resolutionLevel);
  const resolutionLevels = usePlayerStore((state) => state.resolutionLevels);
  const hlsInstance = usePlayerStore((state) => state.hlsInstance);
  const loop = usePlayerStore((state) => state.loop);
  const setCaptionsEnabled = usePlayerStore((state) => state.setCaptionsEnabled);
  const subtitleTrack = usePlayerStore((state) => state.subtitleTrack);
  const subtitleTracks = usePlayerStore((state) => state.subtitleTracks);
  const videoElement = usePlayerStore((state) => state.videoElement);
  const { trackChangedVideoClosedCaptionTrack } = useTrackChangedVideoClosedCaptionTrack();
  const { trackClickedVideoLoopButton } = useTrackClickedVideoLoopButton();
  const { trackClickedVideoQualityButton } = useTrackClickedVideoQualityButton();

  const handlePlaybackRateChange = useCallback(
    (playbackRate: number) => {
      if (videoElement) {
        videoElement.playbackRate = playbackRate;
      }
    },
    [videoElement],
  );

  const handleSubtitleTrackChange = useCallback(
    (trackIndex: number) => {
      if (hlsInstance) {
        hlsInstance.subtitleTrack = trackIndex;
      }

      setCaptionsEnabled(trackIndex >= 0);

      if (trackIndex >= 0) {
        trackChangedVideoClosedCaptionTrack({
          id,
          track: subtitleTracks[trackIndex].name || subtitleTracks[trackIndex].lang || `Track ${trackIndex + 1}`,
        });
      }
    },
    [hlsInstance, id, setCaptionsEnabled, trackChangedVideoClosedCaptionTrack, subtitleTracks],
  );

  const handleSubtitlesOff = useCallback(() => {
    handleSubtitleTrackChange(-1);
  }, [handleSubtitleTrackChange]);

  const selectedSubtitleLabel = useMemo(() => {
    if (subtitleTrack < 0) {
      return 'Off';
    }

    return subtitleTracks[subtitleTrack]?.name || subtitleTracks[subtitleTrack]?.lang || `Track ${subtitleTrack + 1}`;
  }, [subtitleTrack, subtitleTracks]);

  const selectedResolutionLabel = useMemo(() => {
    if (resolutionLevel < 0) {
      return 'Auto';
    }

    const selectedResolution = resolutionLevels[resolutionLevel];

    if (!selectedResolution) {
      return 'Auto';
    }

    return `${Math.min(selectedResolution.width, selectedResolution.height)}p`;
  }, [resolutionLevel, resolutionLevels]);

  const options: DropdownMenuOption[] = useMemo(() => {
    const qualityOptions: DropdownMenuOption[] = [
      {
        id: 'resolution-auto',
        label: 'Auto',
        onSelect: () => {
          if (hlsInstance) {
            hlsInstance.currentLevel = -1;
          }
          trackClickedVideoQualityButton();
        },
        suffix: resolutionLevel === -1 ? <CheckIcon className="ads:text-icon-primary text-blue-9" size="sm" /> : null,
        type: 'item',
      },
    ];

    resolutionLevels.forEach((res, index) => {
      const resolution = Math.min(res.width, res.height);

      qualityOptions.push({
        id: `resolution-${resolution}`,
        label: `${resolution}p`,
        onSelect: () => {
          if (hlsInstance) {
            hlsInstance.currentLevel = index;
          }
          trackClickedVideoQualityButton();
        },
        suffix:
          index === resolutionLevel ? <CheckIcon className="ads:text-icon-primary text-blue-9" size="sm" /> : null,
        type: 'item',
      });
    });

    const optionsArray: DropdownMenuOption[] = [
      {
        id: 'video-quality',
        label: 'Video quality',
        options: qualityOptions,
        suffix: <span className="ads:text-secondary text-12 text-gray-11">{selectedResolutionLabel}</span>,
        type: 'sub',
      },
    ];

    if (includePlaybackRateOptions) {
      optionsArray.push(
        {
          id: 'playback-speed-separator',
          type: 'separator',
        },
        {
          id: 'playback-speed',
          label: 'Playback speed',
          options: PLAYBACK_RATES.map((speed) => ({
            id: `playback-speed-${speed}`,
            label: `${speed}×`,
            onSelect: () => handlePlaybackRateChange(speed),
            suffix:
              speed === playbackRate ? <CheckIcon className="ads:text-icon-primary text-blue-9" size="sm" /> : null,
            type: 'item',
          })),
          suffix: <span className="ads:text-secondary text-12 text-gray-11">{playbackRate}×</span>,
          type: 'sub',
        },
      );
    }

    optionsArray.push(
      {
        id: 'video-playback-separator',
        type: 'separator',
      },
      {
        id: 'loop-video',
        label: 'Loop video',
        type: 'item',
        onSelect: () => {
          if (videoElement) {
            videoElement.loop = !videoElement.loop;
          }
          trackClickedVideoLoopButton();
        },
        suffix: loop ? <CheckIcon className="ads:text-icon-primary text-blue-9" size="sm" /> : null,
      },
    );

    if (includeSubtitleOptions) {
      const subtitleOptions: DropdownMenuOption[] = subtitleTracks.length
        ? [
            {
              id: 'subtitle-track-off',
              label: 'Off',
              onSelect: handleSubtitlesOff,
              suffix:
                subtitleTrack === -1 ? <CheckIcon className="ads:text-icon-primary text-blue-9" size="sm" /> : null,
              type: 'item',
            },
            ...subtitleTracks.map((track, index) => ({
              id: `subtitle-track-${track.id}`,
              label: track.name || track.lang || `Track ${index + 1}`,
              onSelect: () => handleSubtitleTrackChange(index),
              suffix:
                subtitleTrack === index ? <CheckIcon className="ads:text-icon-primary text-blue-9" size="sm" /> : null,
              // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
              type: 'item' as const,
            })),
          ]
        : [
            {
              id: 'subtitle-track-none',
              label: 'No subtitles',
              disabled: true,
              type: 'item',
            },
          ];

      optionsArray.push(
        {
          id: 'subtitles-separator',
          type: 'separator',
        },
        {
          id: 'subtitles',
          label: 'Subtitles',
          options: subtitleOptions,
          suffix: (
            <span className="ads:text-secondary text-12 text-gray-11">
              {subtitleTracks.length ? selectedSubtitleLabel : 'None'}
            </span>
          ),
          type: 'sub',
        },
      );
    }

    if (includeThemeOption && onSelectTheme) {
      optionsArray.push(
        {
          id: 'appearance-separator',
          type: 'separator',
        },
        {
          id: 'appearance',
          label: 'Appearance',
          options: [
            {
              id: 'theme-light',
              label: 'Light mode',
              onSelect: () => onSelectTheme(Theme.Light),
              prefix: <SunIcon size="sm" />,
              suffix:
                theme !== Theme.Dark ? <CheckIcon className="ads:text-icon-primary text-blue-9" size="sm" /> : null,
              type: 'item',
            },
            {
              id: 'theme-dark',
              label: 'Dark mode',
              onSelect: () => onSelectTheme(Theme.Dark),
              prefix: <MoonIcon size="sm" />,
              suffix:
                theme === Theme.Dark ? <CheckIcon className="ads:text-icon-primary text-blue-9" size="sm" /> : null,
              type: 'item',
            },
          ],
          suffix: (
            <span className="ads:text-secondary text-12 text-gray-11">{theme === Theme.Dark ? 'Dark' : 'Light'}</span>
          ),
          type: 'sub',
        },
      );
    }

    return optionsArray;
  }, [
    handlePlaybackRateChange,
    handleSubtitleTrackChange,
    handleSubtitlesOff,
    includePlaybackRateOptions,
    includeSubtitleOptions,
    includeThemeOption,
    loop,
    onSelectTheme,
    playbackRate,
    selectedResolutionLabel,
    selectedSubtitleLabel,
    subtitleTrack,
    subtitleTracks,
    theme,
    trackClickedVideoLoopButton,
    trackClickedVideoQualityButton,
    videoElement,
    hlsInstance,
    resolutionLevels,
    resolutionLevel,
  ]);

  return <SettingsButton options={options} container={container} />;
};

export const VideoSettingsButton = memo(VideoSettingsButtonComponent);

VideoSettingsButton.displayName = 'VideoSettingsButton';
